import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';

import {
  AcquisitionQuery,
  AcquisitionQueryDataOut,
  Client,
  CommandStatus, DownloadAcquisitionDataIn,
  MeterStatus,
  PageInfo,
  PermissionsEnum,
  SortingType,
} from '../../../api/api-reference';
import {
  AcquisitionFilterOutput,
  AcquisitionMonitorFilterComponent,
} from '../../core/components/table/filter-panel/filters/acquisition-monitor-filter/acquisition-monitor-filter.component';
import { IsNewDirective } from '../../../directives/is-new.directive';
import { AuthService } from '../../../services/auth.service';
import { ClipboardService } from '../../../services/clipboard.service';
import { SignalRService } from '../../../services/signalr.service';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { SearchComponent } from '../../core/components/search/search.component';
import { SortComponent } from '../../core/components/sort/sort.component';
import { DateRangeInput } from '../../core/models/date-range-input';
import { PaginationComponent } from '../../core/components/pagination/pagination.component';
import { processDateRange } from '../../../helper/date-utils';
import { AcquisitionDetailsComponent } from './acquisition-details/acquisition-details.component';
import { FileService } from "../../../services/file.service";
import { SignalRBaseComponent } from '../../../services/signalr-base.component';
import { generateFileNameAppendix } from '../../../helper/download-filename';

@Component({
  selector: 'app-acquisition',
  standalone: true,
  imports: [
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    DatepickerComponent,
    SortComponent,
    SearchComponent,
    IsNewDirective,
    PaginationComponent,
    AcquisitionDetailsComponent,
    AcquisitionMonitorFilterComponent,
  ],
  templateUrl: './acquisition.component.html',
  styleUrl: './acquisition.component.scss',
})
export class AcquisitionComponent extends SignalRBaseComponent implements OnInit {
  public pageSize: number = 10;
  public pageNumber: number = 1;
  public currentPage: number = 1;
  public numberOfItems: number = 0;
  public lastPageSize: number = 1;
  public selectedAcquisition: AcquisitionQueryDataOut | undefined;

  selectedFilters: { onlineStatus: MeterStatus | undefined } = { onlineStatus: undefined };

  isDatepickerOpen = false;
  isModalOpen = false;
  readonly dayInMs = 24 * 60 * 60 * 1000;
  PermissionEnum = PermissionsEnum;

  data: AcquisitionQueryDataOut[] = [];
  commandStatusDict: Record<
    string,
    { status: CommandStatus; errorMessage?: string } | undefined
  > = {};
  public readonly CommandStatus = CommandStatus;

  requestData = new AcquisitionQuery({
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.pageNumber }),
    search: undefined,
    startDate: undefined,
    endDate: undefined,
    sorting: SortingType.Descending,
  });

  constructor(
    private client: Client,
    private toastr: ToastrService,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    signalRService: SignalRService,
    private fileService: FileService,
  ) {
    super(signalRService);
    this.reloadTable(1);
  }

  selectedDate: string | null = '';
  onDateRangeChanged(input: DateRangeInput): void {
    const result = processDateRange(input);

    this.requestData.startDate = result.startDate;
    this.requestData.endDate = result.endDate;
    this.selectedDate = result.selectedDate;
    this.filterDatesInTable();
  }

  clearDates(): void {
    this.requestData.startDate = undefined;
    this.requestData.endDate = undefined;
    this.reloadTable();
  }

  filterDatesInTable() {
    this.client.queryAcquisitions(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  protected override registerHandlers(): void {
    const handleMessage = (message: any) => {
      const item = this.data.find((i) => i.meterNumber === message['meterNumber']);
      if (!item?.meterNumber) return;

      this.commandStatusDict[item.meterNumber] = {
        status: message.commandStatus ?? message.status,
        errorMessage: message.errorMessage,
      };

      item.sessionStatus = message.meterIsOnline ?? message.status;
    };

    this.registerSignalREvent('online-status', (msg) => handleMessage(msg));
    this.registerSignalREvent('ping-device', (msg) => handleMessage(msg));
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  private async updateSignalRSubscriptions(): Promise<void> {
    if (!this.data || this.data.length === 0) return;

    const deviceNumbers = this.data
      .map(item => item.meterNumber)
      .filter((no): no is string => !!no);

    await this.replaceDeviceSubscriptions(deviceNumbers);
  }


  reloadTable(page?: number) {
    this.pageNumber = page || 0;
    if (page) this.requestData!.pageInfo!.page = this.pageNumber;

    this.client.queryAcquisitions(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfItems = response.count || 0;
        this.lastPageSize = Math.ceil(this.numberOfItems / this.pageSize);

        this.updateSignalRSubscriptions();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
    this.selectedAcquisition = undefined;
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.reloadTable(this.currentPage);
  }

  searchChange(searchText: string) {
    this.requestData.search = searchText;
    this.reloadTable(1);
  }

  handleRowClick(row: AcquisitionQueryDataOut) {
    if (!this.isModalOpen) {
      this.selectedAcquisition = row;
    }
  }

  toggleModal() {
    this.isDatepickerOpen = !this.isDatepickerOpen;
  }

  onSortOption(selectedSort?: SortingType) {
    if (selectedSort) this.requestData.sorting = selectedSort;
    this.reloadTable();
  }

  onFilterSet(event: AcquisitionFilterOutput) {
    this.selectedFilters.onlineStatus = event.selectedMeterOnlineStatus;
    this.requestData.sessionStatus = event.selectedMeterOnlineStatus;
    this.reloadTable(1);
  }

  retryCommand(row: AcquisitionQueryDataOut, event: MouseEvent) {
    event.stopPropagation();
    if (!row.meterNumber) {
      return; // skip if no meter number
    }

    this.commandStatusDict[row.meterNumber] = { status: CommandStatus.Pending };
    this.client.pingMeter(row.meterNumber).subscribe({
      next: (result) => this.toastr.success(result),
      error: (err) => this.toastr.error(err),
    });
  }

  getErrorMessage(meterNumber?: string): string | null {
    if (!meterNumber) return null;
    return this.commandStatusDict[meterNumber]?.errorMessage ?? null;
  }

  download() {
    const fileNameApendix = generateFileNameAppendix();

    const data = new DownloadAcquisitionDataIn({
      search: this.requestData.search,
      sorting: this.requestData.sorting,
      startDate: this.requestData.startDate,
      endDate: this.requestData.endDate,
      status: this.selectedFilters.onlineStatus,
    });

    this.client.downloadAcquisitionFile(data).subscribe({
      next: (response) => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `${'Acquisitions'}${fileNameApendix}`,
        );
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
